import { useRef, useState, type KeyboardEvent } from 'react'
import { Textarea } from '@/components/ui/textarea'
import type { AvatarPerson } from '@/components/composites/user-avatar'
import { MentionList } from '@/components/composites/mention-list'
import {
  mentionKeyAction,
  useEscapeCloses,
  useMentionHighlight,
  useMentionIds,
} from '@/components/composites/mention-keyboard'

/**
 * A textarea that completes `@` into a colleague's name.
 *
 * It inserts the person's DISPLAY NAME and nothing else — no hidden id, no marker syntax.
 * The API resolves names against real members when the comment is posted, which is where
 * that decision belongs: a browser that could name the recipients could name anyone. It also
 * means a comment reads correctly everywhere it is shown, including the client portal and a
 * Slack digest, neither of which knows what a mention is.
 *
 * The picker is a convenience, not the mechanism. Somebody who types the whole name by hand,
 * or pastes it, gets exactly the same result — which is what stops this becoming a feature
 * you have to know the trick for.
 *
 * Keyboard: ↑/↓ move the highlight, ↵ or Tab insert it, Esc closes — see `mention-keyboard.ts`.
 */
export function MentionTextarea({
  value,
  onChange,
  people,
  onSubmit,
  placeholder,
  rows = 2,
  maxLength,
  bracketMentions = false,
  className,
}: {
  value: string
  onChange: (next: string) => void
  /** Everyone who can be named. Filtered as they type. */
  people: readonly AvatarPerson[]
  /** ⌘↵ / Ctrl↵, when the picker is not open. */
  onSubmit?: () => void
  placeholder?: string
  rows?: number
  maxLength?: number
  /** Insert @[Display Name] instead of @Display Name. Existing comment composers keep false. */
  bracketMentions?: boolean
  className?: string
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  /** Where the live `@` starts, or null when there is not one. */
  const [anchor, setAnchor] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const { listId, optionId } = useMentionIds()

  const matches =
    anchor === null
      ? []
      : people
          .filter((p) => p.displayName.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 6)
  const open = matches.length > 0
  // Keyed on the query: typing returns the highlight to the first match, a keyup does not.
  const { highlighted, setHighlighted, move } = useMentionHighlight(
    matches.length,
    `${anchor ?? ''}:${query}`,
  )

  /**
   * Read the `@…` the cursor is sitting in, if any.
   *
   * Scans back from the caret and gives up at whitespace or a second `@`. The name may
   * contain a space — "Vikas D" has to keep matching — so it allows one, and stops at the
   * point where a sentence has plainly resumed.
   */
  function syncFromCaret(text: string, caret: number) {
    const before = text.slice(0, caret)
    const at = before.lastIndexOf('@')
    if (at === -1) return reset()

    const typed = before.slice(at + 1)
    // Two spaces, a newline, or another @ means the mention ended and this is prose again.
    if (/[\n\r]/.test(typed) || typed.includes('@') || /\s\s/.test(typed) || typed.length > 40) {
      return reset()
    }
    // An `@` directly after a word is an email separator, not a mention.
    if (at > 0 && /[\p{L}\p{N}]/u.test(before[at - 1] ?? '')) return reset()

    setAnchor(at)
    setQuery(typed)
  }

  function reset() {
    setAnchor(null)
    setQuery('')
  }

  // Escape closes the list without also closing the drawer or dialog the composer sits in.
  useEscapeCloses(open, ref, reset)

  function choose(person: AvatarPerson) {
    if (anchor === null) return
    const caret = ref.current?.selectionStart ?? value.length
    // A trailing space, so the next word is not glued to the name — and so the name has the
    // word boundary the server's matcher looks for.
    const inserted = bracketMentions ? `@[${person.displayName}] ` : `@${person.displayName} `
    const next = value.slice(0, anchor) + inserted + value.slice(caret)
    onChange(next)
    reset()
    requestAnimationFrame(() => {
      const to = anchor + inserted.length
      ref.current?.focus()
      ref.current?.setSelectionRange(to, to)
    })
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (open) {
      // While the picker is up it owns these keys — Enter must complete the name rather than
      // post a half-written comment, which is the single most annoying way to get this wrong.
      const action = mentionKeyAction({
        key: e.key,
        keyCode: e.keyCode,
        isComposing: e.nativeEvent.isComposing,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
      })
      if (action) {
        e.preventDefault()
        e.stopPropagation()
        if (action === 'next') return move(1)
        if (action === 'prev') return move(-1)
        if (action === 'close') return reset()
        const person = matches[highlighted]
        if (person) choose(person)
        return
      }
    }
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      onSubmit?.()
    }
  }

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        rows={rows}
        maxLength={maxLength}
        value={value}
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? optionId(highlighted) : undefined}
        onChange={(e) => {
          onChange(e.target.value)
          syncFromCaret(e.target.value, e.target.selectionStart)
        }}
        onKeyUp={(e) => {
          // The picker's own keys were handled on keydown. Re-reading the caret on their keyup
          // would reopen a list that Escape or Enter just closed.
          if (PICKER_KEYS.has(e.key)) return
          syncFromCaret(e.currentTarget.value, e.currentTarget.selectionStart)
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Deferred, or the click that picks somebody unmounts the list before it lands.
          window.setTimeout(reset, 150)
        }}
        placeholder={placeholder}
        className={className}
      />

      {open ? (
        <MentionList
          id={listId}
          optionId={optionId}
          people={matches}
          highlighted={highlighted}
          onHighlight={setHighlighted}
          onChoose={choose}
          className="bottom-full left-0 mb-1 w-64"
        />
      ) : null}
    </div>
  )
}

const PICKER_KEYS = new Set(['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'])
